import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";
import { Link } from "react-router-dom";

function IslamicTradePage19() {
  const contentRef = useRef(null);
   const links = {
    prev: "/islamic-trade-page-18", 
    index: "/index",                 
    next: "/islamic-trade-page-20", 
  };
  const titleText = "Principles of Partnership (Part 2)";
  const questions = [
    {
      text: "1. In an Islamic partnership, what is the rule regarding the distribution of losses??",
      options: [
        {
          value: "A",
          label: "A. Losses can be distributed based on mutual agreement.",
        },
        {
          value: "B",
          label: "B.  Losses are borne entirely by the managing partner.",
        },
        {
          value: "C",
          label: "C. Losses must be shared according to capital contribution.",
        },
        {
          value: "D",
          label: "D. Losses are shared equally regardless of capital.",
        },
      ],
      correct: "D",
    },
    {
      text: "2. According to Islamic jurisprudence, can partners in a valid Sharikah operate in different locations and deal in different goods?",
      options: [
        {
          value: "A",
          label: "A. No, they must operate jointly and sell the same product.",
        },
        {
          value: "B",
          label: "B. Yes, as long as they agree on profit-sharing terms",
        },
        { value: "C", label: "C. Only if their capital is the same in value." },
        { value: "D", label: "D. Only if approved by a Shariah court." },
      ],
      correct: "B",
    },
    {
      text: "3. Which of the following is true about dissolving an Islamic partnership?",
      options: [
        { value: "A", label: "A. It can only be done by mutual agreement." },
        {
          value: "B",
          label:
            "B.  It is allowed by any partner unless restricted by agreement.",
        },
        {
          value: "C",
          label: "C. It is not allowed once profit has been shared.",
        },
        {
          value: "D",
          label: "D. It requires approval from a third-party guardian.",
        },
      ],
      correct: "B",
    },
    {
      text: "4. In Islamic partnerships, what determines the validity of differing types of capital contributions (e.g., livestock vs. machinery)?",
      options: [
        { value: "A", label: "A. The goods must be of equal utility." },
        { value: "B", label: "B. The goods must be of the same species." },
        {
          value: "C",
          label:
            "C. Their values must be agreed upon and profit/loss proportions defined.",
        },
        {
          value: "D",
          label: "D. Different types of goods are never valid in Sharikah",
        },
      ],
      correct: "C",
    },
  ];
  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);
  return (
    <div className="container" ref={contentRef}>
      <p className="content">
        <LandingSection
          titleText={titleText}
          scrollToRef={contentRef}
          progressValue={19}
          timerMinutes={60}
          currentLevel={1}
        />
        Welcome to <b>Day 19</b>! Today, we continue our journey through the
        principles of Sharikah (Partnership) in Islamic finance by exploring
        some of its more nuanced applications. We’ll examine how partnerships
        operate when partners contribute different types of capital, such as
        cash, goods, or effort, and how Islamic law handles profit-sharing in
        such scenarios. We’ll also look at the rules governing separate sales
        locations, the ability to annul partnerships, and how Shariah ensures
        flexibility without compromising justice. By the end of this session,
        you’ll have a clearer understanding of how Islamic partnerships adapt to
        real-world business dynamics while maintaining ethical and equitable
        standards.
        <br />
        <br />
        <b>Sharikah Al-Wujooh: Partnership Based on Reputation</b>
        <br />
        <br />
        Sharikah Al-Wujooh is a form of partnership where individuals leverage
        their good reputation and credibility in the market to secure capital or
        assets. In this arrangement, the partners do not provide financial
        investment directly but instead use their trustworthy standing to
        negotiate terms with asset owners or financiers. <br />
        For example, two individuals may jointly convince a landowner to sell
        them a piece of land with deferred payment terms, based on their
        reputable status in society. The landowner, trusting their integrity,
        agrees, and any profits generated from the land are shared according to
        a pre-agreed ratio. This model aligns with Islamic principles, ensuring
        fairness as long as profit-sharing terms are clearly defined and all
        contractual obligations are honored.
        <br />
        <br />
        <b>Sharikah Abdaan: Partnership Through Collaborative Effort</b>
        <br />
        <br />
        Sharikah Abdaan is a partnership where individuals come together to
        contribute their skills and labor, rather than capital, to accomplish a
        shared goal. This type of partnership emphasizes the value of expertise
        and teamwork in generating profits. <br />
        For example, two tailors might collaborate to stitch shirts, with one
        specializing in collars and sleeves while the other completes the rest
        of the garment. Once the shirts are sold, they divide the profits based
        on a pre-agreed ratio. This arrangement highlights the importance of
        equitable distribution and mutual cooperation, aligning with the
        principles of fairness and shared responsibility in Islamic finance.{" "}
        <br />
        <br />
        <b>Managing Absences in Sharikah Abdaan</b>
        <br />
        <br />
        In Sharikah Abdaan, the partnership framework accommodates situations
        where one partner cannot fulfill their responsibilities due to valid
        reasons such as illness or other unavoidable circumstances. In such
        cases, the present partner has two options: either to continue sharing
        profits with the absent partner or request a substitute to perform the
        tasks during the absence. It becomes obligatory for the absent partner
        to arrange for a substitute, ensuring the continuity of work. If the
        absent partner fails to provide a substitute, the present partner may
        either annul the partnership or retain all profits for the work
        completed during the absence. Conversely, if a partner misses work
        without a valid reason, the other partner has the right to keep the
        profits from that period or dissolve the partnership entirely. These
        rules uphold fairness and accountability, ensuring both partners remain
        committed to their shared responsibilities.
        <br />
        <br />
        <b>Sharikah Mufaawadhah: A Comprehensive Partnership Model</b>
        <br />
        <br />
        Sharikah Mufaawadhah is a versatile and inclusive form of partnership
        that integrates elements from various types of partnerships. It allows
        partners to contribute in diverse ways, such as capital investment,
        physical labor, leveraging reputation, or a combination thereof, to
        achieve common objectives. This structure fosters collaboration by
        accommodating the unique strengths and resources of each partner, making
        it highly adaptable and practical in contemporary business environments.
        <br />
        For instance, consider a partnership to establish a restaurant. One
        partner contributes the financial capital needed to set up the premises
        and procure equipment, while another partner manages daily operations,
        utilizing their expertise in food preparation and service. A third
        partner may use their strong reputation and network to secure favorable
        supplier agreements or attract investors. Each partner's contribution is
        vital to the success of the venture, and the profits are shared based on
        a mutually agreed-upon ratio reflecting their respective inputs.
        <br />
        Sharikah Mufaawadhah’s flexibility and inclusivity make it one of the
        most prevalent partnership models today. It embodies the Islamic
        principles of mutual cooperation and fair profit-sharing, ensuring that
        all partners benefit equitably based on their contributions. This model
        is particularly suited for complex ventures requiring diverse inputs and
        serves as a testament to the enduring relevance of traditional
        partnership concepts in modern economies.
        <br />
        <br />
        <b>Summary of Today's Lesson</b>
      </p>
      <table className="trade-table">
        <thead>
          <tr>
            <th>Type of Partnership</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Sharikah Inaan</td>
            <td>
              Partners contribute both capital and expertise, with profits
              shared according to their agreement.
            </td>
          </tr>
          <tr>
            <td>Musaqah</td>
            <td>
              A partnership where one party tends to another's orchards or
              gardens in exchange for a share of the produce.
            </td>
          </tr>
          <tr>
            <td>Mudarabah</td>
            <td>
              A partnership where one party provides capital, and the other
              contributes expertise or labor, with profits shared.
            </td>
          </tr>
          <tr>
            <td>Muzaara'ah</td>
            <td>
              A partnership where one party provides land, and the other
              cultivates it, sharing the harvest.
            </td>
          </tr>
          <tr>
            <td>Sharikah Abdaan</td>
            <td>
              Partners contribute their labor or skills without investing or
              raising capital.
            </td>
          </tr>
          <tr>
            <td>Sharikah Al-Wujooh</td>
            <td>
              Partners use their reputation to raise capital or secure assets
              for investment.
            </td>
          </tr>
          <tr>
            <td>Sharikah Mufaawadhah</td>
            <td>
              A combination of all types of partnerships, where partners
              contribute in various ways.
            </td>
          </tr>
        </tbody>
      </table>
      <p className="content">
        <b>Coming up next</b>
        <br />
        <br />
        In our next session, we will explore the rulings on Hiring and Leasing
        (Ijaarah), a vital aspect of Islamic commercial law that governs rental
        and service-based contracts. From renting properties to hiring
        individuals for specific tasks, Ijaarah plays a significant role in
        day-to-day financial interactions. We will examine its conditions,
        evidence from the Quran and Sunnah, how it differs from sale contracts,
        and special scenarios like waqf property rentals and contracts affected
        by death or asset loss. Join us on Day 20 as we unpack this ethically
        guided system that promotes fairness, clarity, and justice in rental and
        employment agreements.
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>
      <p className="content">
        1. In an Islamic partnership, what is the rule regarding the
        distribution of losses?
        <ul>
          <li>A. Losses can be distributed based on mutual agreement</li>
          <li>B. Losses are borne entirely by the managing partner</li>
          <li>C. Losses must be shared according to capital contribution</li>
          <li>D. Losses are shared equally regardless of capital</li>
        </ul>
        2. According to Islamic jurisprudence, can partners in a valid Sharikah
        operate in different locations and deal in different goods?
        <ul>
          <li>A. No, they must operate jointly and sell the same product</li>
          <li>B. Yes, as long as they agree on profit-sharing terms</li>
          <li>C. Only if their capital is the same in value</li>
          <li>D. Only if approved by a Shariah court</li>
        </ul>
        3. Which of the following is <b>true</b> about dissolving an Islamic
        partnership?
        <ul>
          <li>A. It can only be done by mutual agreement </li>
          <li>
            B. It is allowed by any partner unless restricted by agreement
          </li>
          <li>C. It is not allowed once profit has been shared</li>
          <li>D. It requires approval from a third-party guardian</li>
        </ul>
        4. In Islamic partnerships, what determines the validity of differing
        types of capital contributions (e.g., livestock vs. machinery)?
        <ul>
          <li>A. The goods must be of equal utility</li>
          <li>B. The goods must be of the same species</li>
          <li>
            C. Their values must be agreed upon and profit/loss proportions
            defined
          </li>
          <li>D. Different types of goods are never valid in Sharikah</li>
        </ul>
      </p>

     <FloatingPageNav 
                  prevLink={links.prev} 
                  indexLink={links.index} 
                  nextLink={links.next} 
                />
    </div>
  );
}

export default IslamicTradePage19;
